import { Box, CircularProgress, Grid } from '@mui/material';
import { Button } from '@mui/material';
import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';

import {
  EntityAttribute,
  EntityAttributes,
  useEntityAttributes,
} from '~/data/attributes';
import { AllEnums, Enum, useAllEnums } from '~/data/enums';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/components/FormComponent/useStyles';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { EditCustomDataFields } from '~/legacy-components/CustomDataFields';

const DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH = 5;
interface EmploymentDetailsCustomDataProps {
  control: Control<Form>;
  index: number;
  allEnums: AllEnums;
  pendingProposal?: boolean;
}

type SequenceAttribute = {
  sequence: number;
};

type OrderedEntityAttribute = EntityAttribute & SequenceAttribute;

type RenderCustomFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  index: number;
  fieldName: string;
  fieldType: string;
  label: string;
  enumOptionsKey?: string;
  disabled?: boolean;
};

const RenderControlledCustomField = <TFieldValues extends FieldValues>(
  props: RenderCustomFieldProps<TFieldValues>,
) => {
  const {
    control,
    index,
    fieldName,
    fieldType,
    label,
    enumOptionsKey,
    disabled,
  } = props;

  const formFieldName = `schedule.${index}.employmentDetails.customData.${fieldName}`;

  const { allEnums } = useAllEnums();

  const hasOptions =
    !!enumOptionsKey &&
    !!allEnums[enumOptionsKey] &&
    allEnums[enumOptionsKey].length > 0;
  const enumOptions: Enum[] = hasOptions ? allEnums[enumOptionsKey] : [];

  return (
    <EditCustomDataFields
      name={label}
      inputProps={{
        options: enumOptions,
        name: formFieldName,
        fullWidth: true,
        disabled,
      }}
      type={fieldType}
      control={control as Control<FieldValues>}
    />
  );
};

export const EmploymentDetailsCustomData = ({
  control,
  index,
  allEnums,
  pendingProposal,
}: EmploymentDetailsCustomDataProps) => {
  const [emplCustomDataFields, setEmplCustomDataFields] =
    useState<EntityAttributes>();
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [customFieldsShowAreaLength, setCustomFieldsShowAreaLength] =
    useState<number>(DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH);
  const { allAttributes, isLoading } = useEntityAttributes();

  const sxStyles = useSxStyles();
  useEffect(() => {
    setFetchLoading(isLoading);
    if (_.isEmpty(allAttributes)) return;
    setEmplCustomDataFields(allAttributes);
    return () => {
      setEmplCustomDataFields({} as EntityAttributes);
    };
  }, [allAttributes, isLoading]);

  const CUSTOM_DATA_FIELDS = allEnums['CUSTOM_ENTITY_EMPLOYMENT_DETAILS'];
  //@ts-ignore
  //eslint-disable-next-line
  const emplCustomDataFieldsArray = emplCustomDataFields as Array;

  const emplOrderedCustomDataFields: OrderedEntityAttribute[] =
    emplCustomDataFieldsArray
      ?.map((field: EntityAttribute): OrderedEntityAttribute => {
        const foundEnum = CUSTOM_DATA_FIELDS?.find(
          (customField) =>
            customField?.code === field?.fCustomDataAttributeName,
        );
        const sequence = foundEnum?.seq || 0;
        // Quikcfix: Apply translated label, instead of untranslated term.
        // TODO: EntityAttribute should probably be fixed in BE to provide the translated label.
        const translatedLabel =
          foundEnum?.name || field?.fCustomDataAttributeLabel;

        return {
          ...field,
          sequence,
          fCustomDataAttributeLabel: translatedLabel,
        };
      })
      .sort(
        (a: OrderedEntityAttribute, b: OrderedEntityAttribute) =>
          a.sequence - b.sequence,
      )
      ?.slice(0, customFieldsShowAreaLength);

  const emplDetailsLength =
    (Array.isArray(emplCustomDataFields) && emplCustomDataFields.length) || 0;
  if (fetchLoading)
    return (
      <p>
        <br />
        <CircularProgress size={50} />
      </p>
    );

  return (
    <>
      {Array.isArray(emplCustomDataFields) && emplCustomDataFields.length > 0 && (
        <Box sx={sxStyles.eclusiveRow}>
          <h4>{`Custom fields - ${
            customFieldsShowAreaLength > emplDetailsLength
              ? emplDetailsLength
              : customFieldsShowAreaLength
          } / ${emplDetailsLength}`}</h4>
        </Box>
      )}
      {
        //@ts-ignore
        emplOrderedCustomDataFields?.map(
          (field: EntityAttribute, fieldIndex) => {
            return (
              <Grid
                item
                key={`renderedField-${fieldIndex}-fragment`}
                sx={{ width: '280px' }}
              >
                <RenderControlledCustomField
                  key={`renderedField-${fieldIndex}-new`}
                  control={control}
                  index={index}
                  fieldName={field.fCustomDataAttributeName}
                  fieldType={field.fCustomDataAttributeDataType}
                  label={field.fCustomDataAttributeLabel}
                  enumOptionsKey={field.fCustomDataAttributeDataSubtype}
                  disabled={pendingProposal}
                />
              </Grid>
            );
          },
        )
      }
      <Box sx={sxStyles.eclusiveRow}>
        {emplDetailsLength > customFieldsShowAreaLength && (
          <Button
            color="primary"
            fullWidth
            onClick={() => {
              setCustomFieldsShowAreaLength((prev) =>
                prev + DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH >
                emplDetailsLength
                  ? emplDetailsLength
                  : prev + DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH,
              );
            }}
          >
            Show more
          </Button>
        )}
        {customFieldsShowAreaLength >
          DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH && (
          <Button
            color="primary"
            fullWidth
            onClick={() => {
              setCustomFieldsShowAreaLength((prev) =>
                prev - DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH <
                DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH
                  ? DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH
                  : prev - DEFAULT_CUSTOM_DATA_FIELDS_SHOW_LENGTH,
              );
            }}
          >
            Show less
          </Button>
        )}
      </Box>
    </>
  );
};
